import useFetchBillInterest from '../../../hooks/useFetchBillInterest'
import { BillDetail } from '../../../interfaceTypes/common'
import { formatCurrency } from '../../../utils'
import { calculateClientBillData } from '../../../utils/billing'
import { Box, Typography, ListItem, Avatar, Chip, styled, ListItemButton } from '@mui/material'
import { Person } from '@mui/icons-material'
import { enumToLabel } from '../../../utils'
import theme from '../../../theme'
import useFetchOutstandingBills from '../../../hooks/useFetchOutstandingBills'

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '12px 16px',
  transition: 'all 0.2s ease-in-out',
  '&.Mui-selected': {
    backgroundColor: 'rgba(119, 17, 23, 0.08)',
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    '&:after': {
      borderBottom: 'none'
    }
  }
}))

interface BillClientsMenuProps {
  bill: BillDetail
  selectedClientId: string | undefined | null
  accessToken: NonNullable<NonNullable<BillDetail>['accessTokens']>[number]
  onClientSelect: (clientId: string) => void
}

const BillClientMenuItem = ({ bill, selectedClientId, accessToken, onClientSelect }: BillClientsMenuProps) => {
  const { client, amount, token } = accessToken || {}
  const { interestAccrued } = useFetchBillInterest({
    billId: bill?.id,
    clientId: client?.id,
    billType: bill?.type,
    token
  })
  const { previousBalance } = useFetchOutstandingBills({
    caseId: bill?.case?.id,
    billId: bill?.id,
    billStatus: bill?.status,
    billType: bill?.type,
    clientId: client?.id,
    token
  })

  const clientWrittenOff = (accessToken?.writtenOffPrincipal || 0) + (accessToken?.writtenOffInterest || 0)
  const billWrittenOff = (bill?.writtenOffPrincipal || 0) + (bill?.writtenOffInterest || 0)

  const { payableAmount: clientPayableAmount } = calculateClientBillData({
    paymentAllocations: bill?.paymentAllocations || [],
    previousBalance: previousBalance,
    bill: {
      amount: bill?.amount,
      splitBill: !!bill?.case?.billing?.splitBillEnabled,
      status: bill?.status,
      discount: bill?.discount,
      interestAccrued,
      writtenOff: billWrittenOff
    },
    client: { id: client?.id, share: amount, writtenOff: clientWrittenOff }
  })

  const isSelected = selectedClientId === client?.id
  const clientName = client?.displayName || ''
  const caseExternalUserToExternalUserRole = bill?.case?.caseToExternalUserToExternalUserRole?.find(
    role => role.externalUserToExternalUserRole.externalUser?.id === client?.id
  )

  if (!client) {
    return null
  }

  return (
    <ListItem disablePadding>
      <StyledListItemButton selected={isSelected} onClick={() => onClientSelect(client.id)}>
        <Avatar
          sx={{
            backgroundColor: isSelected ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.12)'
          }}
        >
          {clientName ? clientName.charAt(0).toUpperCase() : <Person />}
        </Avatar>

        <Box flex={1}>
          <Typography fontWeight={500} fontSize="15px" color={isSelected ? theme.palette.primary.main : 'text.primary'}>
            {clientName} {client?.clientNumber && `(#${client?.clientNumber})`} -{' '}
            {enumToLabel(caseExternalUserToExternalUserRole?.caseRole || '')}
          </Typography>
        </Box>

        <Chip
          label={formatCurrency(clientPayableAmount || 0)}
          size="small"
          color={isSelected ? 'primary' : 'default'}
        />
      </StyledListItemButton>
    </ListItem>
  )
}

export default BillClientMenuItem
